import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import useUrlQuery from 'global/helperFunctions/useQuery';
import { IBackendSearchGeneric } from 'global/Models/globalModels';
import ApiService from 'global/utils/ApiService';
import { useCallback, useMemo } from 'react';

interface UseApiListOptions<TData> extends Omit<UseQueryOptions<IBackendSearchGeneric<TData>>, 'queryKey' | 'queryFn'> {
  /** Fixed payload merged into every request (e.g. customerId) */
  fixedPayload?: Record<string, any>;
  /** Transform the URL query before sending (e.g. rename/remap keys) */
  transformPayload?: (_query: Record<string, any>) => Record<string, any>;
  /** Additional path appended to the endpoint */
  additionalPath?: string;
  /** Stable base key prefix for cache invalidation. When provided, the query key becomes [...baseQueryKey, endpoint, apiPayload] */
  baseQueryKey?: readonly unknown[];
  /** Override the default query key builder */
  queryKeyOverride?: readonly unknown[];
}

/**
 * Drop-in React Query hook for list/search pages.
 *
 * Combines URL query params (pagination, filters) with React Query data fetching.
 * Designed to replace the manual `useState + ApiService` pattern used in TcListPageWrapper.
 *
 * Features:
 * - URL-driven pagination & filters (via useUrlQuery)
 * - Automatic refetch when URL params change
 * - Pagination helpers (handlePagination, pageNumber, recordsPerPage, total)
 * - Refetch function for after mutations
 *
 * @example
 * const { data, isLoading, pagination } = useApiList<client>(
 *   endpointUrls.clientGetAll,
 *   {
 *     baseQueryKey: queryKeys.clientList,
 *     fixedPayload: { customerId: 123 },
 *   }
 * );
 *
 * // data?.data          -> client[]
 * // data?.totalItemCount -> number
 * // pagination.handlePagination(2, 20) -> updates URL, triggers refetch
 */
export function useApiList<TData = any>(endpoint: string, options: UseApiListOptions<TData> = {}) {
  const { fixedPayload, transformPayload, additionalPath, baseQueryKey, queryKeyOverride, ...queryOptions } = options;

  // URL query params (pagination, filters from the URL)
  const urlQuery = useUrlQuery();

  // Build the API payload from URL params + fixed payload
  const apiPayload = useMemo(() => {
    const payload = transformPayload ? transformPayload(urlQuery.query) : urlQuery.query;
    return { ...payload, ...fixedPayload };
  }, [urlQuery.query, fixedPayload, transformPayload]);

  // Build React Query key from URL params
  const queryKey = useMemo(() => {
    if (queryKeyOverride) return queryKeyOverride;
    if (baseQueryKey) return [...baseQueryKey, endpoint, apiPayload];
    return [endpoint, apiPayload];
  }, [endpoint, apiPayload, baseQueryKey, queryKeyOverride]);

  const finalEndpoint = additionalPath ? endpoint + additionalPath : endpoint;

  const query = useQuery<IBackendSearchGeneric<TData>>({
    queryKey,
    queryFn: async () => {
      const response = await ApiService.post(finalEndpoint, apiPayload);
      return response as IBackendSearchGeneric<TData>;
    },
    ...queryOptions,
  });

  // Pagination helpers (delegate to URL query, which triggers refetch)
  const pagination = useMemo(
    () => ({
      pageNumber: urlQuery.pageNumber,
      recordsPerPage: urlQuery.recordsPerPage,
      total: query.data?.totalItemCount || 0,
      handlePagination: urlQuery.handlePagination,
    }),
    [urlQuery.pageNumber, urlQuery.recordsPerPage, urlQuery.handlePagination, query.data?.totalItemCount]
  );

  // Set URL filters (triggers refetch via key change)
  const setFilters = useCallback(
    (values: Record<string, any>) => {
      urlQuery.setQuery({ ...values, recordsPerPage: urlQuery.recordsPerPage });
    },
    [urlQuery]
  );

  // Reset filters to defaults
  const resetFilters = useCallback(
    (defaultValues: Record<string, any> = {}) => {
      urlQuery.resetQuary({ recordsPerPage: urlQuery.recordsPerPage || 10, ...defaultValues });
    },
    [urlQuery]
  );

  const refetch = useCallback(() => {
    query.refetch();
  }, [query.refetch]);

  return {
    ...query,
    pagination,
    setFilters,
    resetFilters,
    refetch,
    /** Raw URL query object (for form field initial values) */
    urlQuery,
  };
}

export default useApiList;
